// InviteSubmit — consent-tier selection and final submit.

import { useState } from 'react'
import { useLocation, useParams } from 'wouter'
import { getStoredLang, submitInvitation, type ConsentTier } from '@/lib/api'
import { t as tr } from '@/lib/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

interface TierOption {
  value: ConsentTier
  label: string
  detail: string
  fields: ('name' | 'role' | 'city' | 'handle')[]
}

const TIERS: TierOption[] = [
  {
    value: 'anon',
    label: 'Anonymous',
    detail: 'No name, no city, no handle. Paraphrased only.',
    fields: [],
  },
  {
    value: 'first_name_role_city',
    label: 'First name, role, city',
    detail: 'e.g. "Sam, designer in Auckland".',
    fields: ['name', 'role', 'city'],
  },
  {
    value: 'named_handle',
    label: 'Named + handle',
    detail: 'Full attribution with a social handle you choose.',
    fields: ['name', 'role', 'city', 'handle'],
  },
]

export function InviteSubmit() {
  const params = useParams() as { token: string }
  const token = params.token
  const [, setLocation] = useLocation()

  const [tier, setTier] = useState<ConsentTier>('anon')
  const [name, setName] = useState('')
  const [role, setRole] = useState('')
  const [city, setCity] = useState('')
  const [handle, setHandle] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selected = TIERS.find((t) => t.value === tier)!

  async function onSubmit() {
    setSubmitting(true)
    setError(null)
    try {
      await submitInvitation(token, {
        consent_tier: tier,
        respondent_name: selected.fields.includes('name') ? name || null : null,
        respondent_role: selected.fields.includes('role') ? role || null : null,
        respondent_city: selected.fields.includes('city') ? city || null : null,
        respondent_handle: selected.fields.includes('handle') ? handle || null : null,
      })
      setLocation(`/i/${token}/done`)
    } catch (e) {
      setError((e as Error).message || 'submit_failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="min-h-screen bg-cream px-4 sm:px-6 py-8 sm:py-12">
      <div className="mx-auto max-w-xl space-y-8">
        <header className="space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {tr(getStoredLang(), 'final_step')}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl">
            {tr(getStoredLang(), 'how_to_credit')}
          </h1>
          <p className="font-serif text-foreground/80 leading-relaxed">
            {tr(getStoredLang(), 'consent_note')}
          </p>
        </header>

        <RadioGroup
          value={tier}
          onValueChange={(v) => setTier(v as ConsentTier)}
          className="space-y-3"
        >
          {TIERS.map((t) => {
            const isSelected = tier === t.value
            return (
              <div
                key={t.value}
                className={`rounded-md border transition-colors ${
                  isSelected
                    ? 'border-vermillion bg-white'
                    : 'border-black/10 bg-white/40 hover:bg-white/70'
                }`}
              >
                <label
                  htmlFor={`tier-${t.value}`}
                  className="flex gap-3 p-4 cursor-pointer"
                >
                  <RadioGroupItem value={t.value} id={`tier-${t.value}`} className="mt-1" />
                  <div className="space-y-1">
                    <p className="font-serif text-lg leading-none">{t.label}</p>
                    <p className="text-sm text-muted-foreground">{t.detail}</p>
                  </div>
                </label>

                {isSelected && t.fields.length > 0 && (
                  <div className="space-y-4 px-4 pb-4 pt-1 border-t border-vermillion/20 animate-in fade-in slide-in-from-top-1 duration-200">
                    {t.fields.includes('name') && (
                      <div className="space-y-1.5">
                        <Label htmlFor="f-name">First name</Label>
                        <Input
                          id="f-name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Sam"
                        />
                      </div>
                    )}
                    {t.fields.includes('role') && (
                      <div className="space-y-1.5">
                        <Label htmlFor="f-role">Role</Label>
                        <Input
                          id="f-role"
                          value={role}
                          onChange={(e) => setRole(e.target.value)}
                          placeholder="Designer"
                        />
                      </div>
                    )}
                    {t.fields.includes('city') && (
                      <div className="space-y-1.5">
                        <Label htmlFor="f-city">City</Label>
                        <Input
                          id="f-city"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          placeholder="Auckland"
                        />
                      </div>
                    )}
                    {t.fields.includes('handle') && (
                      <div className="space-y-1.5">
                        <Label htmlFor="f-handle">Handle</Label>
                        <Input
                          id="f-handle"
                          value={handle}
                          onChange={(e) => setHandle(e.target.value)}
                          placeholder="@you"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </RadioGroup>

        {error && (
          <p className="text-sm text-destructive">{tr(getStoredLang(), 'err_submit')}</p>
        )}

        <div className="flex items-center justify-between gap-3 pt-4 border-t border-black/5">
          <Button
            variant="secondary"
            onClick={() => setLocation(`/i/${token}/review`)}
            disabled={submitting}
          >
            {tr(getStoredLang(), 'back')}
          </Button>
          <Button
            size="lg"
            onClick={onSubmit}
            disabled={submitting}
            className="bg-vermillion hover:bg-vermillion/90 text-white"
          >
            {submitting ? tr(getStoredLang(), 'submitting') : tr(getStoredLang(), 'submit')}
          </Button>
        </div>
      </div>
    </main>
  )
}

export default InviteSubmit
